import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import POSDashboard from './dashboard';
import POSBookingDetail from './booking-detail';
import POSMenuManagement from './menu-management';
import POSTimeManagement from './time-management';
/**
 * POS Routes
 * Requires authenticated user with ADMIN, STAFF, or SALES role
 */
export default function POSRoutes() {
    const { user, isLoading } = useAuth();
    // Wait for auth to finish loading
    if (isLoading) {
        return (_jsx("div", { className: "min-h-screen bg-slate-900 flex items-center justify-center", children: _jsx("p", { className: "text-slate-300", children: "Loading..." }) }));
    }
    // Require authentication and ADMIN, STAFF, or SALES role
    if (!user) {
        return _jsx(Navigate, { to: "/login", replace: true });
    }
    if (user.role !== 'ADMIN' && user.role !== 'STAFF' && user.role !== 'SALES') {
        return (_jsx("div", { className: "min-h-screen flex items-center justify-center p-4", children: _jsxs("div", { className: "text-center", children: [_jsx("h1", { className: "text-2xl font-bold text-slate-900 mb-2", children: "Access Denied" }), _jsx("p", { className: "text-slate-600 mb-4", children: "You need staff or admin privileges to access the POS system." }), _jsx("button", { onClick: () => window.history.back(), className: "text-blue-600 hover:text-blue-700 font-medium", children: "\u2190 Go Back" })] }) }));
    }
    return (_jsxs(Routes, { children: [_jsx(Route, { path: "dashboard", element: _jsx(POSDashboard, {}) }), _jsx(Route, { path: "booking/:id", element: _jsx(BookingDetailWrapper, {}) }), _jsx(Route, { path: "menu", element: _jsx(MenuManagementWrapper, {}) }), _jsx(Route, { path: "time-management", element: _jsx(TimeManagementWrapper, {}) }), _jsx(Route, { path: "*", element: _jsx(Navigate, { to: "dashboard", replace: true }) })] }));
}
// Wrapper to extract route param and provide onBack navigation
function BookingDetailWrapper() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    if (!id)
        return _jsx(Navigate, { to: "/pos/dashboard", replace: true });
    const params = new URLSearchParams(location.search);
    const ocrReturnUrl = params.get('returnTo') === 'ocr-analysis'
        ? `/admin/receipt-analysis?${new URLSearchParams({
            date: params.get('ocrDate') || '',
            filter: params.get('ocrFilter') || 'needs_review',
            paymentId: params.get('ocrPaymentId') || '',
        }).toString()}`
        : null;
    return (_jsx(POSBookingDetail, { bookingId: id, onBack: () => navigate(ocrReturnUrl || '/pos/dashboard'), backLabel: ocrReturnUrl ? 'OCR Analysis' : undefined }));
}
// Wrapper to provide onBack navigation
function MenuManagementWrapper() {
    const navigate = useNavigate();
    const { user } = useAuth();
    if (user?.role === 'SALES')
        return _jsx(Navigate, { to: "/pos/dashboard", replace: true });
    return _jsx(POSMenuManagement, { onBack: () => navigate('/pos/dashboard') });
}
// Wrapper for time management (admin only)
function TimeManagementWrapper() {
    const navigate = useNavigate();
    const { user } = useAuth();
    if (user?.role !== 'ADMIN')
        return _jsx(Navigate, { to: "/pos/dashboard", replace: true });
    return _jsx(POSTimeManagement, { onBack: () => navigate('/pos/dashboard') });
}
